import axiosInstance from "../axiosInstance";

const M = "api/maintenance/refunds";

export interface RefundLot {
  order_id: number;
  amount: number;
  consumed: number;
  remaining: number;
  created_at: string;
  age_days: number;
  in_window: boolean;
  cf_payment_id: string | null;
  gateway_order_id: string | null;
  payment_group: string | null;
  upi_id: string | null;
  looks_cashfree: boolean;
}

export interface GateCheck {
  check: string;
  ok: boolean;
  detail: string;
}

export interface Quote {
  refundable: number;
  clawback: number;
  balance_after: number;
  limited_by: string;
  caps: Record<string, string>;
}

export interface WalletLookup {
  wallet_id: number;
  user_id: number | null;
  display_name: string | null;
  phone: string | null;
  org_name: string | null;
  user_status: string | null;
  balance: number;
  cash_balance: number;
  promo_balance: number;
  live_sessions: number;
  open_requests: number;
  balance_scale: number;
  lots: RefundLot[];
  suggested_order_id: number | null;
  quote: Quote | null;
  gate: GateCheck[];
  clear_to_refund: boolean;
}

export interface SourceRefundResult {
  refund_request_id: number;
  gateway_status: string | null;
  gateway_error: string | null;
  pg_refund_id: string;
  amount: number;
  clawback: number;
  balance_after: number;
  cashfree: Record<string, string | null>;
  legs: Record<string, number>;
}

export interface RefundRow {
  id: number;
  refund_request_guid: string;
  user_wallet_id: number;
  kind: string;
  status: string;
  amount: number;
  clawback_amount: number;
  payment_order_id: number | null;
  reason_code: string | null;
  reason_note: string | null;
  pg_refund_id: string | null;
  pg_status: string | null;
  performed_by: string | null;
  created_at: string;
  executed_at: string | null;
  settled_at: string | null;
  failure_detail: string | null;
  who: string | null;
  phone: string | null;
}

export const lookupWallet = (q: string): Promise<WalletLookup> =>
  axiosInstance.get<WalletLookup>(`${M}/lookup`, { params: { q } }).then((r) => r.data);

export const listRefunds = (state?: string): Promise<RefundRow[]> =>
  axiosInstance.get<RefundRow[]>(M, { params: state ? { state } : {} }).then((r) => r.data);

export const sourceRefund = (body: {
  wallet_id: number; order_id: number; amount: number;
  reason_code: string; reason_note?: string;
}): Promise<SourceRefundResult> =>
  axiosInstance.post<SourceRefundResult>(`${M}/source`, body).then((r) => r.data);

export interface RefundDetail extends RefundRow {
  our_order_id: string | null;
  lot_amount: number; cash_consumed: number; cash_refunded: number;
  lot_paid_at: string; payment_group: string | null; upi_id: string | null;
  current_balance: number; cash_balance: number; promo_balance: number;
  gate_snapshot: any; pg_raw: any;
  refund_arn: string | null;
  cf_refund_id: string | null;
  gateway_processed_at: string | null;
  legs: Array<{
    id: number; type: string; amount: number;
    cash_amount: number | null; promo_amount: number | null;
    transaction_state: string; wallet_balance_before: number;
    wallet_balance_after: number; created_date: string;
  }>;
}

export const getRefund = (id: number): Promise<RefundDetail> =>
  axiosInstance.get<RefundDetail>(`${M}/${id}`).then((r) => r.data);

export const refreshRefund = (id: number): Promise<{
  refund_request_id: number; status: string; verdict: string;
  pg_status: string | null; detail?: string; checked: boolean;
}> => axiosInstance.post(`${M}/${id}/refresh`).then((r) => r.data);

export interface PendingReview {
  id: number; user_wallet_id: number; amount: number; clawback_amount: number;
  reason_code: string | null; reason_note: string | null; created_at: string;
  quote_shown: any; performed_by: string | null; raised_by: string;
  payment_order_id: number; our_order_id: string | null;
  lot_amount: number; lot_paid_at: string; lot_age_days: number;
  upi_id: string | null; who: string | null; phone: string | null;
  current_balance: number; cash_balance: number; promo_balance: number;
}

export interface AuditRow {
  id: number; at: string; actor: string; actor_type: string; action: string;
  from_status: string | null; to_status: string | null; detail: any;
}

export interface RefundSettings {
  self_serve_enabled: boolean;
  self_serve_window_days: number;
  gateway_window_days: number;
  self_serve_org_ids: number[];
  reviewer_emails: string; alert_to: string; alert_from: string;
  customer_terms: string[]; submitted_message: string; recharge_notice: string;
  updated_by: string | null; updated_at: string;
}

export const reviewQueue = (): Promise<PendingReview[]> =>
  axiosInstance.get<PendingReview[]>(`${M}/queue/pending`).then((r) => r.data);

export const refundAudit = (id: number): Promise<AuditRow[]> =>
  axiosInstance.get<AuditRow[]>(`${M}/${id}/audit`).then((r) => r.data);

export const approveRefund = (id: number) =>
  axiosInstance.post(`${M}/${id}/approve`).then((r) => r.data);

export const rejectRefund = (id: number, reason: string) =>
  axiosInstance.post(`${M}/${id}/reject`, { outcome: "failed", failure_detail: reason })
    .then((r) => r.data);

export const getRefundSettings = (): Promise<RefundSettings> =>
  axiosInstance.get<RefundSettings>(`${M}/settings/current`).then((r) => r.data);

export const putRefundSettings = (body: Partial<RefundSettings>): Promise<RefundSettings> =>
  axiosInstance.put<RefundSettings>(`${M}/settings/current`, body).then((r) => r.data);

export const settleRefund = (
  id: number,
  body: { outcome: "succeeded" | "failed"; pg_status?: string; failure_detail?: string },
): Promise<RefundRow> =>
  axiosInstance.post<RefundRow>(`${M}/${id}/settle`, body).then((r) => r.data);
